import { useEffect, useRef, useState } from "react";
import { LuCheckCheck } from "react-icons/lu";
import AddressForm from "../../components/checkout/AddressForm";
import OrderSummary from "../../components/checkout/OrderSummary/OrderSummary";
import Payment from "../../components/checkout/Payment";
import Page from "../../components/page/Page.jsx";
import SummaryContainer from "../../components/SummaryContainer/SummaryContainer.jsx";
import { getAdditionalRate } from "../../services/OnlineStore";
import useCartStore from "../../states/CartStore";
import styles from "./Checkout.module.css";

const checkoutSummaryClasses = {
    container: styles.summaryContainer,
    header: styles.summaryHeader,
    title: styles.summaryTitle,
    content: styles.summaryContent,
    actionButton: styles.summaryAction,
    actionButtonIcon: styles.summaryActionIcon,
};

const emptyAddress = {
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    email: "",
    phone: "",
};

function isAddressComplete(address) {
    return Object.keys(emptyAddress).every((field) => {
        const value = address?.[field];
        return typeof value === "string" ? value.trim().length > 0 : Boolean(value);
    });
}

function Checkout() {
    const cart = useCartStore((state) => state.cart);
    const [shippingRate, setShippingRate] = useState(null);
    const [taxCents, setTaxCents] = useState(null);
    const [step, setStep] = useState(1);
    const [checkoutError, setCheckoutError] = useState(null);
    const [billingAddressAutofill, setBillingAddressAutofill] = useState(true);
    const [paymentReady, setPaymentReady] = useState(false);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [deliveryAddress, setDeliveryAddress] = useState(emptyAddress);
    const [billingAddress, setBillingAddress] = useState(emptyAddress);
    const [resolvedBillingAddress, setResolvedBillingAddress] = useState(deliveryAddress);
    const paymentRef = useRef(null);

    useEffect(() => {
        setResolvedBillingAddress(billingAddressAutofill ? deliveryAddress : billingAddress);
    }, [billingAddressAutofill, deliveryAddress, billingAddress]);

    const handleDeliveryAddressComplete = async (address = deliveryAddress) => {
        const resolvedAddress = address && typeof address === "object" && !address.target
            ? address
            : deliveryAddress;

        if (!isAddressComplete(resolvedAddress)) return;

        setCheckoutError(null);
        setBillingAddress(resolvedAddress);
        setStep(2);

        try {
            const zipCode = String(resolvedAddress?.zip || "").trim();
            const quote = await getAdditionalRate(cart?.cart_token, zipCode, cart?.items?.length || 0, resolvedAddress);
            setShippingRate(quote?.rate ?? 0);
            setTaxCents(quote?.tax?.tax_cents ?? 0);

            console.log("Shipping quote response:", quote);
        } catch (error) {
            console.error("Shipping quote request failed:", error);
            setShippingRate(0);
            setTaxCents(0);
            setCheckoutError("Failed to fetch shipping rate. Please try again later.");
        }
    };

    const handleBillingAddressComplete = () => {
        if (!billingAddressAutofill && !isAddressComplete(billingAddress)) return;
        setStep(3);
    };

    const steps = {
        1: {
            title: "Delivery Address",
            actions: [{
                title: "Continue",
                action: handleDeliveryAddressComplete,
                style: "next",
                disabled: !isAddressComplete(deliveryAddress),
            }],
            completed: isAddressComplete(deliveryAddress),
        },
        2: {
            title: "Billing Address",
            actions: [{
                title: "Continue",
                action: handleBillingAddressComplete,
                style: "next",
                disabled: !billingAddressAutofill && !isAddressComplete(billingAddress),
            }],
            completed: billingAddressAutofill
                ? isAddressComplete(deliveryAddress)
                : isAddressComplete(billingAddress),
        },
        3: { title: "Payment" },
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className={styles.deliveryAddress}>
                        <AddressForm address={deliveryAddress} setAddress={setDeliveryAddress} />
                    </div>
                );
            case 2:
                return (
                    <div className={styles.billingAddress}>
                        <label className={styles.billingAddressAutofillCheckboxContainer}>
                            <input
                                type="checkbox"
                                checked={billingAddressAutofill}
                                onChange={(event) => setBillingAddressAutofill(event.target.checked)}
                            />
                            Same as delivery address
                        </label>
                        {billingAddressAutofill ? (
                            <div className={styles.billingAddressAutofillContainer}>
                                <p>{deliveryAddress.firstName} {deliveryAddress.lastName}</p>
                                <p>{deliveryAddress.address}</p>
                                <p>{deliveryAddress.city}, {deliveryAddress.state} {deliveryAddress.zip}</p>
                            </div>
                        ) : (
                            <AddressForm address={billingAddress} setAddress={setBillingAddress} />
                        )}
                    </div>
                );
            case 3:
                return (
                    <Payment
                        ref={paymentRef}
                        deliveryInfo={deliveryAddress}
                        billingInfo={resolvedBillingAddress}
                        onReadyChange={setPaymentReady}
                        onProcessingChange={setPaymentProcessing}
                        onError={setCheckoutError}
                    />
                );
            default:
                return null;
        }
    };

    const currentStepActions = steps[step]?.actions || [];

    return (
        <Page
            title="Checkout"
            skinny={true}
            pageClassesOverride={{
                container: styles.checkoutPage,
                content: styles.checkoutPageContent,
                contentSkinny: styles.checkoutPageContent,
                title: styles.checkoutTitle,
            }}
        >
            <div className={styles.checkoutContent}>
                <div className={styles.checkoutForm}>
                    <div className={styles.stepHeaderContainer}>
                        {Object.keys(steps).map((stepNumber) => (
                            <h3
                                key={stepNumber}
                                className={`${styles.stepHeader} ${Number(stepNumber) === step ? styles.active : ""}`}
                            >
                                {steps[stepNumber].title}
                                <span>
                                    {steps[stepNumber].completed && (
                                        <LuCheckCheck size={20} className={styles.completedIcon} />
                                    )}
                                </span>
                            </h3>
                        ))}
                    </div>

                    {renderStep()}

                    <div className={styles.checkoutActions}>
                        <div>
                            {step !== 1 && (
                                <button
                                    type="button"
                                    onClick={() => setStep((currentStep) => Math.max(currentStep - 1, 1))}
                                    className={`${styles.backButton} ${styles.actionButton}`}
                                >
                                    Previous
                                </button>
                            )}
                        </div>
                        {currentStepActions.map((action) => (
                            <button
                                key={action.title}
                                type="button"
                                onClick={action.action}
                                disabled={action.disabled}
                                className={`${styles[`${action.style}Button`]} ${styles.actionButton}`}
                            >
                                {action.title}
                            </button>
                        ))}
                    </div>
                </div>

                <SummaryContainer
                    title="Order Summary"
                    actionButtonTitle={paymentProcessing ? "Processing..." : "Pay Now"}
                    onAction={() => paymentRef.current?.submit()}
                    disabled={step !== 3 || !paymentReady || paymentProcessing}
                    error={checkoutError}
                    cartSummaryClassesOverride={checkoutSummaryClasses}
                >
                    <OrderSummary cart={cart} shippingRate={shippingRate} step={step} />
                </SummaryContainer>
            </div>
        </Page>
    );
}

export default Checkout;
